import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Network, Crown, Building2, Landmark, Pickaxe, Compass, Leaf, Waves, TreePine,
  Wheat, Cable, Bolt, Anchor, Fish, Bus, HeartPulse, Scale, GraduationCap,
  Palmtree, Home, Users, ShoppingBag, Briefcase, ChevronRight, Sparkles, Radar,
  Globe2, Layers,
} from "lucide-react";

/**
 * Phase XV — Sovereign Government Operating Model™
 * Presentation-only hub. Tabs: Federation · Government Hierarchy ·
 * Federation Readiness · Future Placeholders · International Positioning.
 */
const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";
const AMBER = "#E4A93C";
const RESERVED = "#5A7A8A";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Chip = ({ children, color = BLUE, filled = false }) => (
  <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono uppercase text-[9.5px] tracking-[0.14em]"
        style={{ background: filled ? color : color + "20", color: filled ? "#071B2B" : color, border: "1px solid " + color + "45" }}>
    {children}
  </span>
);

const PLATFORM_ICON = {
  executive_government: Crown, land360: Landmark, mine360: Pickaxe, survey360: Compass,
  environment360: Leaf, water360: Waves, forestry360: TreePine, agriculture360: Wheat,
  infrastructure360: Cable, energy360: Bolt, marine360: Anchor, fisheries360: Fish,
  transport360: Bus, health360: HeartPulse, justice360: Scale, education360: GraduationCap,
  tourism360: Palmtree, housing360: Home, labour360: Briefcase, trade360: ShoppingBag,
};

const STATUS_COLOR = { active: GREEN, reserved: AMBER, placeholder: RESERVED };

const HIERARCHY_KIND_COLORS = {
  state: PURPLE, executive: PURPLE, collective: PURPLE, ministry: GOLD,
  official: BLUE, cluster: BLUE, department: BLUE, authority: TEAL,
  agency: TEAL, province: GREEN, district: GREEN, local_authority: GREEN,
  state_enterprise: GOLD, strategy: PURPLE,
};

function renderHierarchyNodes(nodes, depth, openMap, toggle) {
  return nodes.map((node) => {
    const hasKids = (node.children || []).length > 0;
    const open = openMap[node.id] ?? (depth < 1);
    const color = HIERARCHY_KIND_COLORS[node.kind] || BLUE;
    return (
      <div key={node.id} style={{ marginLeft: depth * 12 }} className="py-0.5">
        <div className="flex items-center gap-2 text-[12px]">
          <button onClick={() => toggle(node.id, !open)}
                  className="w-3 h-3 rounded-sm flex items-center justify-center text-[8px]"
                  style={{ background: hasKids ? color + "30" : "transparent", color: hasKids ? color : "transparent" }}>
            {hasKids ? (open ? "-" : "+") : ""}
          </button>
          <div className="w-1 h-3 rounded-sm" style={{ background: color }} />
          <div className="text-[#F6F8FA]">{node.label}</div>
          <Chip color={color}>{node.kind}</Chip>
        </div>
        {open && hasKids && renderHierarchyNodes(node.children, depth + 1, openMap, toggle)}
      </div>
    );
  });
}

function HierarchyView({ tree }) {
  const [openMap, setOpenMap] = useState({});
  const toggle = (id, val) => setOpenMap((m) => ({ ...m, [id]: val }));
  return (
    <Card title="Government Structure Intelligence™" accent={PURPLE} testId="xv-hierarchy">
      <div className="max-h-[600px] overflow-auto pr-2">
        {renderHierarchyNodes([tree], 0, openMap, toggle)}
      </div>
    </Card>
  );
}

export const SovereignOperatingModel = () => {
  const [tab, setTab] = useState("federation");
  const [arch, setArch] = useState(null);
  const [hier, setHier] = useState(null);
  const [fed, setFed] = useState(null);
  const [future, setFuture] = useState(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, h, f, fu, p] = await Promise.all([
          axios.get(`${API}/cadastre360/phase-xv/architecture`,        { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/government-structure`,{ headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/federation-readiness`,{ headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/future-expansion`,    { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xv/positioning`,         { headers: auth() }),
        ]);
        setArch(a.data); setHier(h.data); setFed(f.data); setFuture(fu.data); setPos(p.data);
      } catch (e) {
        toast.error("Failed to load Phase XV — " + (e.response?.data?.detail || e.message));
      }
    })();
  }, []);

  const TABS = [
    { key: "federation", label: "Federated Architecture", icon: Network },
    { key: "hierarchy",  label: "Government Structure",   icon: Building2 },
    { key: "readiness",  label: "Federation Readiness",   icon: Radar },
    { key: "future",     label: "Future Government",      icon: Layers },
    { key: "positioning",label: "Commercial Positioning", icon: Globe2 },
  ];

  return (
    <div className="p-6 space-y-4" data-testid="page-sovereign-operating-model">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: PURPLE + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${PURPLE}30 100%)` }}>
        <Network size={38} className="mt-1" style={{ color: PURPLE }} />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: PURPLE }}>
            Cadastre360™ · Phase XV · Sovereign Government Operating Model™
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            Federated National Sovereign Government Platform
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            Cadastre360 elevated from a set of platforms into a National Government Operating Model —
            deployable one ministry at a time, scalable to whole-of-government. Zero regression · zero duplication.
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <Chip color={GREEN} filled>Federation Ready</Chip>
          <Chip color={GOLD} filled>Cabinet Ready</Chip>
          <Chip color={BLUE} filled>Investor Ready</Chip>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={"px-3 py-1.5 text-[11.5px] flex items-center gap-1.5 font-medium border-b-2 " +
                             (tab === t.key ? "" : "border-transparent text-[#7C93A5]")}
                  style={tab === t.key ? { borderColor: PURPLE, color: "#F6F8FA" } : {}}
                  data-testid={`tab-${t.key}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "federation" && arch && (
        <>
          <Card title={`Federated Platforms · active ${arch.counts.active} · reserved ${arch.counts.reserved} · placeholders ${arch.counts.placeholder}`} accent={GOLD} testId="xv-federation">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {arch.federated_platforms.map((p) => {
                const Icon = PLATFORM_ICON[p.key] || Layers;
                const c = STATUS_COLOR[p.status];
                return (
                  <div key={p.key} className="rounded-sm border p-2.5" style={{ borderColor: c + "45", background: "rgba(7,27,43,0.55)" }} data-testid={`platform-${p.key}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={14} style={{ color: c }} />
                      <div className="text-[12.5px] font-semibold text-[#F6F8FA]">{p.label}</div>
                      <Chip color={c} filled>{p.status}</Chip>
                    </div>
                    <div className="text-[10.5px] text-[#B3C2CC]">Supports: {p.supports?.join(" · ") || "—"}</div>
                    {p.capabilities && <div className="text-[10.5px] text-[#7C93A5] mt-1">Capabilities: {p.capabilities.join(", ")}</div>}
                  </div>
                );
              })}
            </div>
          </Card>
          <Card title="Deployment Modes" accent={BLUE}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {arch.deployment_modes.map((m) => (
                <div key={m} className="rounded-sm border px-2 py-1.5 flex items-center gap-2" style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.5)" }}>
                  <Sparkles size={11} style={{ color: BLUE }} /> <span className="text-[12px] text-[#F6F8FA]">{m}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Shared Enterprise Services" accent={GREEN}>
            <div className="flex flex-wrap gap-1">
              {arch.shared_services.map((s) => <Chip key={s} color={GREEN}>{s}</Chip>)}
            </div>
          </Card>
        </>
      )}

      {tab === "hierarchy" && hier && (
        <HierarchyView tree={hier.tree} />
      )}

      {tab === "readiness" && fed && (
        <>
          <Card title={`Federation Readiness · ${fed.deployment_readiness_pct}%`} accent={GREEN} testId="xv-readiness">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
              {[["Installed", fed.installed_platforms?.length, GREEN],
                ["Licensed", fed.licensed_platforms?.length, GREEN],
                ["Connected ministries", fed.connected_ministries?.length, BLUE],
                ["Reserved", fed.future_reserved_platforms?.length, AMBER]].map(([l, v, c]) => (
                <div key={l} className="rounded-sm border px-2 py-1.5" style={{ borderColor: c + "40", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">{l}</div>
                  <div className="font-bold text-[16px]" style={{ color: c }}>{v ?? "—"}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
              {["api_health", "digital_twin", "identity", "workflow", "notifications", "digital_trust", "knowledge_repository"].map((k) => (
                <div key={k} className="rounded-sm border px-2 py-1" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.45)" }}>
                  <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">{k.replace(/_/g, ' ')}</div>
                  <div className="text-[12px] text-[#F6F8FA]">{String(fed[k])}</div>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Connected Ministries" accent={BLUE}>
            <div className="flex flex-wrap gap-1">
              {fed.connected_ministries?.map((m) => <Chip key={m} color={BLUE}>{m}</Chip>)}
            </div>
          </Card>
        </>
      )}

      {tab === "future" && future && (
        <Card title="Future Government Expansion™ · reserved & placeholder platforms" accent={AMBER} testId="xv-future">
          <div className="text-[11.5px] text-[#DCE3E8] mb-2">{future.notice}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {future.reserved_platforms.map((p) => {
              const Icon = PLATFORM_ICON[p.key] || Layers;
              const c = STATUS_COLOR[p.status] || RESERVED;
              return (
                <div key={p.key} className="rounded-sm border p-2.5" style={{ borderColor: c + "45", background: "rgba(7,27,43,0.55)" }} data-testid={`future-${p.key}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={14} style={{ color: c }} />
                    <div className="text-[12.5px] font-semibold text-[#F6F8FA]">{p.label}</div>
                    <Chip color={c} filled>{p.status}</Chip>
                  </div>
                  <div className="text-[10.5px] text-[#B3C2CC]">{p.supports?.join(" · ") || p.domain || "future"}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {tab === "positioning" && pos && (
        <>
          <Card title="International Commercial Positioning™" accent={GOLD} testId="xv-positioning">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
              {pos.deployment_modes.map((m) => (
                <div key={m} className="rounded-sm border px-2 py-1.5" style={{ borderColor: GOLD + "40", background: "rgba(7,27,43,0.5)" }}>
                  <div className="text-[12px] text-[#F6F8FA]">{m}</div>
                </div>
              ))}
            </div>
            <div className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#7C93A5] mb-1">Value proposition</div>
            <ul className="text-[12px] text-[#F6F8FA] list-disc pl-5 space-y-0.5">
              {pos.value_proposition.map((v) => <li key={v}>{v}</li>)}
            </ul>
            <div className="mt-3 font-mono uppercase text-[10px] tracking-[0.18em] text-[#7C93A5] mb-1">Audiences</div>
            <div className="flex flex-wrap gap-1">{pos.audiences.map((a) => <Chip key={a} color={PURPLE}>{a}</Chip>)}</div>
          </Card>
        </>
      )}

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] pt-1">
        Cadastre360™ · Phase XV · federated national sovereign platform · additive · zero regression
      </div>
    </div>
  );
};

export default SovereignOperatingModel;
